import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Loader2 } from 'lucide-react';

// The visible area shrinks by more than this when a mobile keyboard opens.
const KEYBOARD_MIN_HEIGHT = 150;

// Reusable Dialog component defined in the same file
type CustomDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isLoading?: boolean;
  title: string;
  description: string;
  children?: React.ReactNode;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  isConfirmDisabled?: boolean;
};

export const CustomDialog = ({
  isOpen,
  setIsOpen,
  isLoading,
  title,
  description,
  children,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isConfirmDisabled = false
}: CustomDialogProps) => {
  const [keyboardViewport, setKeyboardViewport] = useState<{ top: number; height: number } | null>(null);

  // iOS keeps the layout viewport full height when the keyboard opens, so a centered
  // dialog hides its buttons behind it. Re-center in the visible area while the keyboard is up.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!isOpen || !viewport) return;

    const handleViewportChange = () => {
      const keyboardHeight = document.documentElement.clientHeight - viewport.height;
      if (keyboardHeight > KEYBOARD_MIN_HEIGHT) {
        setKeyboardViewport({ top: viewport.offsetTop, height: viewport.height });
      } else {
        setKeyboardViewport(null);
      }
    };

    handleViewportChange();
    viewport.addEventListener('resize', handleViewportChange);
    viewport.addEventListener('scroll', handleViewportChange);
    return () => {
      viewport.removeEventListener('resize', handleViewportChange);
      viewport.removeEventListener('scroll', handleViewportChange);
      setKeyboardViewport(null);
    };
  }, [isOpen]);

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    //setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="xs:max-w-[300px] md:max-w-[400px]"
        style={
          keyboardViewport
            ? {
                top: keyboardViewport.top + keyboardViewport.height / 2,
                maxHeight: keyboardViewport.height - 16,
              }
            : undefined
        }
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="py-4">
          {children}
        </div>

        <DialogFooter className="flex flex-row justify-end space-x-2">
          <Button variant="outline" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} className='w-24 outline-none'>
            {cancelText}
          </Button>
          {onConfirm && (
            <Button onClick={(e) => { e.stopPropagation(); handleConfirm(); }} className='w-24 bg-primary' disabled={isLoading || isConfirmDisabled}>
              {isLoading && <Loader2 className="animate-spin" />}
              {!isLoading && confirmText}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
